'use client';

import { ReactNode, useMemo } from 'react';
import { NextIntlClientProvider, useMessages } from 'next-intl';
import { onI18nError, addAvailableNamespaces } from '@/i18n/on-i18n-error';
import { env } from '@/utils/env';

function deepMerge(
  target: Record<string, any>,
  ...sources: Record<string, any>[]
): Record<string, any> {
  const result = { ...target };
  for (const source of sources) {
    for (const key of Object.keys(source)) {
      if (
        result[key] &&
        source[key] &&
        typeof result[key] === 'object' &&
        typeof source[key] === 'object' &&
        !Array.isArray(result[key]) &&
        !Array.isArray(source[key])
      ) {
        result[key] = deepMerge(result[key], source[key]);
      } else {
        result[key] = source[key];
      }
    }
  }
  return result;
}

interface RouteMessagesProviderProps {
  messages: Record<string, any>;
  children: ReactNode;
}

export default function RouteMessagesProvider({ messages, children }: RouteMessagesProviderProps) {
  const parentMessages = useMessages() as Record<string, any>;

  const mergedMessages = useMemo(
    () => deepMerge({}, parentMessages, messages),
    [parentMessages, messages],
  );

  // 路由切换时，将按需加载的新 namespace 追加到追踪集合
  useMemo(() => addAvailableNamespaces(messages), [messages]);

  return (
    <NextIntlClientProvider
      locale={env.NEXT_PUBLIC_SITE_LANG}
      messages={mergedMessages}
      onError={onI18nError}
    >
      {children}
    </NextIntlClientProvider>
  );
}
